import type { MigrationContext, NamedMigration } from '@pkg/db/types'
import type { Migration, MigrationProvider } from 'kysely'

export type MigrationScope = 'server' | 'client'

export interface AppMigratorProviderConfig {
  migrations: Array<Promise<{ migration: NamedMigration }>>
  scope?: MigrationScope
}

export class AppMigratorProvider implements MigrationProvider {
  constructor(private readonly config: AppMigratorProviderConfig) {}

  async getScopedMigrations() {
    const scope = this.config.scope ?? 'server'
    return await Promise.all(this.config.migrations).then(r =>
      r
        .map(m => m.migration)
        // Filter by scope
        .filter(m => m.scope === 'server-client' || m.scope === scope),
    )
  }

  async getMigrations() {
    const scope = this.config.scope ?? 'server'
    const engine = scope === 'server' ? 'postgres' : 'sqlite'
    const context = { scope, engine } as MigrationContext
    return await this.getScopedMigrations().then(r =>
      // Filter out repeatable migrations (handled separately)
      r
        .filter(m => !m.repeatable)
        .reduce(
          (acc, migration) => {
            const up = migration.up
            const down = migration.down
            acc[migration.name] = {
              ...migration,
              up: db => up(db, context),
              down: down ? db => down(db, context) : undefined,
            }
            return acc
          },
          {} as Record<string, Migration>,
        ),
    )
  }

  async getRepeatableMigrations(): Promise<NamedMigration[]> {
    return await this.getScopedMigrations().then(r => r.filter(m => !!m.repeatable))
  }
}
